import React from 'react';
import { Flex } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFourGiftCardMessagesAsOneString } from '../../bff-api/utils/basket-utils';
import { ProductPrice } from '../../components/product-price';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { ResponsiveImage } from '../../components/responsive-image';
import { useBuildURL } from '../../hooks/useBuildURL';
import { useRecommendations } from '../../hooks/useRecommendations';
import { Text } from '../../ui-components';
import { Drawer } from '../../ui-components/drawer';
import { isGiftCard } from '../../utils/product-utils';
import { useProductsAddedToBagDrawer } from './useProductsAddedToBagDrawer';

const id = 'products-added-to-bag-drawer';

export const ProductsAddedToBagDrawer: React.FC = () => {
    const { isOpenDrawer, onCloseDrawer, productsAddedToBag } = useProductsAddedToBagDrawer();

    const intl = useIntl();
    const { buildCartURL } = useBuildURL();
    const cartUrl = buildCartURL();

    const recommendationWidgets = useRecommendations({
        location: RecommendationLocation.DRAWER,
        productId: productsAddedToBag?.[0]?.master?.masterId,
        categoryId: productsAddedToBag?.[0]?.c_primaryCategory?.id,
        brandCategoryId: productsAddedToBag?.[0]?.c_brandCategoryId,
        collectionId: productsAddedToBag?.[0]?.c_collectionId,
    });

    if (!productsAddedToBag || productsAddedToBag.length === 0) {
        return null;
    }

    const quantity = productsAddedToBag.reduce(
        (accumulator, product) => accumulator + (product.c_quantityAddedToBag ?? 0),
        0,
    );

    return (
        <Drawer
            id={id}
            title={intl.formatMessage(
                {
                    id: 'product.added.to.bag.title',
                    defaultMessage: '+{quantity} Item added to bag',
                },
                {
                    quantity,
                },
            )}
            isOpen={isOpenDrawer}
            onClose={onCloseDrawer}
            callToAction={{
                children: intl.formatMessage({
                    id: 'product.added.to.bag.view.bag',
                    defaultMessage: 'View Bag',
                }),
                onClick: onCloseDrawer,
                href: cartUrl,
            }}
            secondaryCallToAction={{
                children: intl.formatMessage({
                    id: 'product.added.to.bag.continue.shopping',
                    defaultMessage: 'Continue Shopping',
                }),
                onClick: onCloseDrawer,
            }}
            containerStyle={{
                display: 'flex',
                flexDirection: 'column',
            }}
            innerBodyClassName="flex-1"
            shouldToAddMarginBottom={false}
            closeButtonAddCircle={false}
        >
            <Flex direction="column" height="100%" justifyContent="space-between" gap={16}>
                <Flex data-testid={`${id}-products`} direction="column" gap={8}>
                    {productsAddedToBag.map((product) => {
                        const { id: productId, name, c_variationModel, c_imageUrl } = product;
                        const productName = parse(name!) as string;
                        const isGiftCardProduct = isGiftCard(product);
                        const giftMessage = getFourGiftCardMessagesAsOneString(product);

                        return (
                            <Flex
                                data-testid={`${id}-product-${productId}`}
                                key={`${id}-product-${productId}`}
                                gap={4}
                            >
                                <Flex
                                    maxHeight="100px"
                                    width="100px"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                >
                                    {c_imageUrl ? (
                                        <ResponsiveImage
                                            id={`${id}-product-${productId}-image`}
                                            src={c_imageUrl}
                                            alt={productName}
                                            width="100px"
                                            height="100px"
                                        />
                                    ) : null}
                                </Flex>

                                <Flex direction="column" gap={4}>
                                    <Flex direction="column" gap={2} flexGrow={1}>
                                        <Text id={`${id}-product-${productId}-name`}>
                                            {productName}
                                        </Text>

                                        {c_variationModel ? (
                                            <Flex gap={1}>
                                                <Text
                                                    id={`${id}-product-${productId}-variant-label`}
                                                    style="bold"
                                                >
                                                    {c_variationModel.name}
                                                </Text>

                                                <Text
                                                    id={`${id}-product-${productId}-variant-value`}
                                                >
                                                    {c_variationModel.value}
                                                </Text>
                                            </Flex>
                                        ) : null}

                                        {isGiftCardProduct && quantity > 1 && (
                                            <Flex gap={1}>
                                                <Text style="bold">
                                                    <FormattedMessage
                                                        id="product.added.to.bag.quantity"
                                                        defaultMessage="Qty"
                                                    />
                                                </Text>
                                                <Text id={`product-${productId}-quantity`}>
                                                    {quantity}
                                                </Text>
                                            </Flex>
                                        )}

                                        <ProductPrice
                                            id={`${id}-product-${productId}-price`}
                                            prices={product.prices}
                                            price={product.c_gcAmount || product.price}
                                            currency={product.currency}
                                            size="base"
                                        />
                                    </Flex>
                                    {isGiftCardProduct && (
                                        <Flex direction="column" gap={2}>
                                            {product.c_gcRecipientEmail && (
                                                <Flex gap={1}>
                                                    <Text style="bold">
                                                        <FormattedMessage
                                                            id="product.added.to.bag.to"
                                                            defaultMessage="To"
                                                        />
                                                    </Text>
                                                    <Text id={`product-${productId}-to`}>
                                                        {product.c_gcRecipientEmail}
                                                    </Text>
                                                </Flex>
                                            )}
                                            {!!giftMessage && (
                                                <Flex gap={1} direction="column">
                                                    <Text style="bold">
                                                        <FormattedMessage
                                                            id="product.added.to.bag.message"
                                                            defaultMessage="Message"
                                                        />
                                                    </Text>
                                                    <Text id={`product-${productId}-message`}>
                                                        {giftMessage}
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Flex>
                                    )}
                                </Flex>
                            </Flex>
                        );
                    })}
                </Flex>

                {recommendationWidgets || null}
            </Flex>
        </Drawer>
    );
};
